import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { Supplier, SupplierInsert, SupplierUpdate } from '@/types/supplier';
import { toast } from 'sonner';

interface SupplierState {
  suppliers: Supplier[];
  loading: boolean;

  loadSuppliers: () => Promise<void>;
  addSupplier: (supplier: SupplierInsert) => Promise<Supplier | null>;
  updateSupplier: (id: string, updates: SupplierUpdate) => Promise<void>;
  deleteSupplier: (id: string) => Promise<void>;
  getSupplierById: (id: string) => Supplier | undefined;
}

export const useSupplierStore = create<SupplierState>()((set, get) => ({
  suppliers: [],
  loading: false,

  loadSuppliers: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('name');

      if (error) throw error;
      set({ suppliers: (data || []) as Supplier[] });
    } catch (error) {
      console.error('Error loading suppliers:', error);
      toast.error('Failed to load suppliers');
    } finally {
      set({ loading: false });
    }
  },

  addSupplier: async (supplier) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return null;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return null;
      }

      const { data, error } = await supabase
        .from('suppliers')
        .insert({ ...supplier, org_id: profile.org_id })
        .select()
        .single();

      if (error) throw error;

      set(state => ({ suppliers: [...state.suppliers, data as Supplier] }));
      toast.success('Supplier created');
      return data as Supplier;
    } catch (error) {
      console.error('Error creating supplier:', error);
      toast.error('Failed to create supplier');
      return null;
    }
  },

  updateSupplier: async (id, updates) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        suppliers: state.suppliers.map(s => s.id === id ? { ...s, ...updates } : s)
      }));
      toast.success('Supplier updated');
    } catch (error) {
      console.error('Error updating supplier:', error);
      toast.error('Failed to update supplier');
    }
  },

  deleteSupplier: async (id) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        suppliers: state.suppliers.filter(s => s.id !== id)
      }));
      toast.success('Supplier deleted');
    } catch (error) {
      console.error('Error deleting supplier:', error);
      toast.error('Failed to delete supplier');
    }
  },

  getSupplierById: (id) => {
    return get().suppliers.find(s => s.id === id);
  },
}));
